import { Flame, Snowflake, LogOut, Radio, RefreshCw, User, Users, Package, Settings } from "lucide-react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { WorkspaceToggle } from "@/components/WorkspaceToggle";
import { GroupsList } from "@/components/GroupsList";
import { useRefresherCount } from "@/hooks/useRefresherCount";
import { useUserRole } from "@/hooks/useUserRole";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { getCurrentUser } from "@/lib/authUser";
import { requestWithAuthRecovery } from "@/lib/authRequest";

const baseNavigationItems = [
  { title: "Hot", path: "hot", icon: Flame },
  { title: "Cold", path: "cold", icon: Snowflake },
  { title: "Refresher", path: "refresher", icon: RefreshCw },
  { title: "Products", path: "products", icon: Package },
  { title: "Setup", path: "setup", icon: Settings },
];

export function TopNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { workspace, selectedGroupId, setSelectedGroupId } = useWorkspace();
  useUserRole();
  const refresherCount = useRefresherCount();
  const autoSelectedRef = useRef(false);

  // Eagerly resolve & navigate to the user's top group as soon as the app loads
  // in groups workspace, so the popover doesn't have to be opened first.
  useEffect(() => {
    if (workspace !== "groups") return;
    if (selectedGroupId) return;
    if (autoSelectedRef.current) return;
    autoSelectedRef.current = true;

    (async () => {
      const user = await getCurrentUser();
      if (!user) return;

      const [{ data: groups, error: groupsError }, { data: profile }] = await Promise.all([
        requestWithAuthRecovery(() => supabase
          .from("groups")
          .select("id, group_members!inner(user_id)")
          .eq("group_members.user_id", user.id)
          .order("created_at", { ascending: false })),
        requestWithAuthRecovery(() => supabase
          .from("profiles")
          .select("group_order")
          .eq("user_id", user.id)
          .maybeSingle()),
      ]);

      if (groupsError) {
        autoSelectedRef.current = false;
        return;
      }
      if (!groups || groups.length === 0) return;

      const order = (profile?.group_order as string[] | null) || [];
      const sorted = [...groups].sort((a: any, b: any) => {
        const ia = order.indexOf(a.id);
        const ib = order.indexOf(b.id);
        if (ia !== -1 && ib !== -1) return ia - ib;
        if (ia !== -1) return -1;
        if (ib !== -1) return 1;
        return 0;
      });

      const firstId = (sorted[0] as any).id;
      setSelectedGroupId(firstId);
      const currentPath = location.pathname.split("/").pop() || "hot";
      const validTab = ["hot", "cold", "refresher", "products", "setup"].includes(currentPath)
        ? currentPath
        : "hot";
      navigate(`/dashboard/groups/${firstId}/${validTab}`, { replace: true });
    })();
  }, [workspace, selectedGroupId, navigate, location.pathname, setSelectedGroupId]);

  const pathPrefix = "/dashboard";

  const navigationItems = baseNavigationItems.map((item) => ({
    ...item,
    url:
      workspace === "groups" && selectedGroupId
        ? `${pathPrefix}/groups/${selectedGroupId}/${item.path}`
        : workspace === "groups"
          ? `${pathPrefix}/groups`
          : `${pathPrefix}/personal/${item.path}`,
  }));

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  return (
    <header className="border-b border-border bg-background/95 backdrop-blur-md sticky top-0 z-40 pt-safe">
      {/* Row 1: branding + workspace + account */}
      <div className="px-4 h-12 flex items-center justify-between gap-4">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          aria-label="Home"
        >
          <Radio className="h-5 w-5 text-primary" />
          <span className="font-display text-base font-semibold tracking-tight">EchoCRM</span>
        </button>

        <div className="flex items-center gap-2">
          <WorkspaceToggle />
          {workspace === "groups" && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Users className="h-4 w-4" />
                  <span className="hidden sm:inline">My Groups</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <GroupsList />
              </PopoverContent>
            </Popover>
          )}
        </div>

        <div className="flex items-center gap-1">
          <ThemeToggle />
          <Button variant="ghost" size="icon" onClick={() => navigate(`${pathPrefix}/profile`)} title="Profile">
            <User className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={handleLogout} title="Log out">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Row 2: tab nav */}
      <nav
        className="px-4 flex items-center gap-1 h-10 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      >
        {navigationItems.map((item) => (
          <NavLink
            key={item.title}
            to={item.url}
            className={({ isActive }) =>
              cn(
                "relative flex items-center gap-1.5 px-3 h-10 text-sm font-medium whitespace-nowrap transition-colors",
                "border-b-2 -mb-px",
                isActive
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )
            }
          >
            <item.icon className="h-4 w-4" />
            <span>{item.title}</span>
            {item.path === "refresher" && refresherCount > 0 && (
              <Badge
                variant="destructive"
                className="ml-1 h-4 min-w-4 px-1 flex items-center justify-center text-[10px]"
              >
                {refresherCount}
              </Badge>
            )}
          </NavLink>
        ))}
      </nav>
    </header>
  );
}